import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, MessageCircle, Check, ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO, useSettings } from "@/lib/hooks";
import RequestForm from "@/components/sections/RequestForm";
import FAQSection from "@/components/sections/FAQSection";

export default function CityPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const s = useSettings();
  const [city, setCity] = useState(null);
  useEffect(() => {
    api.get(`/zones/${slug}`).then((r) => setCity(r.data)).catch(() => setCity(false));
  }, [slug]);

  useSEO({
    title: city?.meta_title,
    description: city?.meta_description,
  });

  if (city === false) return <div className="text-center py-32 text-txt-500">Zone introuvable.</div>;
  if (!city) return <div className="text-center py-32 text-txt-500">Chargement...</div>;

  const cityName = city.city || city.name;
  const dept = city.department_code || city.code;

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Depann7 - ${cityName}`,
    image: "https://images.unsplash.com/photo-1686966933735-305bd8fe0a77",
    telephone: s?.company_phone || "",
    address: { "@type": "PostalAddress", addressLocality: cityName, addressRegion: "Île-de-France", addressCountry: "FR" },
    areaServed: cityName,
    openingHours: "Mo-Su 00:00-23:59",
  };

  return (
    <div className="bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="bg-ink-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-6">
          <div className="inline-flex items-center gap-2 text-brand-300 text-xs uppercase tracking-widest font-bold">
            <MapPin size={14} /> {dept ? `Département ${dept}` : "Île-de-France"}
          </div>
          <h1 className="text-3xl lg:text-5xl font-extrabold mt-3">{city.h1 || `Dépannage remorquage à ${cityName} 24h/24`}</h1>
          <p className="text-ink-200 mt-5 max-w-3xl text-lg leading-relaxed">{city.seo_text}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a href={`tel:${s?.company_phone_raw || ""}`} className="bg-white text-brand-800 hover:bg-brand-50 font-bold rounded-xl px-6 py-3.5 inline-flex items-center justify-center gap-2 transition-colors"><Phone size={18} /> Appel urgent</a>
            <a href={`https://wa.me/${s?.company_whatsapp || ""}`} target="_blank" rel="noreferrer" className="bg-whats hover:bg-whats-hover text-white font-bold rounded-xl px-6 py-3.5 inline-flex items-center justify-center gap-2 transition-colors"><MessageCircle size={18} /> WhatsApp</a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
            <h2 className="text-lg font-extrabold text-txt-900">Services disponibles à {cityName}</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {[
                "Dépannage batterie / panne sèche",
                "Remorquage véhicule en panne",
                "Remorquage suite à accident",
                "Transport de véhicule",
                "Sortie de fourrière",
                "Assistance routière 24h/24",
              ].map((a) => (
                <li key={a} className="flex items-start gap-2 text-txt-700 text-sm">
                  <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-0.5"><Check className="text-green-600" size={12} /></div>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
            <h2 className="text-lg font-extrabold text-txt-900">Tarifs indicatifs</h2>
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
              <Tariff label="Forfait prise en charge" value="89 €" />
              <Tariff label="Prix au kilomètre" value="2,10 €" />
              <Tariff label="Majoration nuit (22h-7h)" value="+40 €" />
              <Tariff label="Majoration dimanche / férié" value="+30 €" />
            </div>
            <Link to="/tarifs" className="text-brand text-sm font-bold mt-4 inline-flex items-center gap-1 hover:underline">Voir tous les tarifs <ArrowRight size={14} /></Link>
          </div>
        </div>

        <div className="bg-white border border-surface-200 rounded-2xl shadow-card overflow-hidden h-fit"><RequestForm compact /></div>
      </div>

      <FAQSection />
    </div>
  );
}

function Tariff({ label, value }) {
  return (
    <div className="flex items-center justify-between bg-white border border-surface-200 rounded-xl px-4 py-3">
      <span className="text-txt-500">{label}</span>
      <span className="text-lg font-extrabold text-brand">{value}</span>
    </div>
  );
}
